import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import {
  DataTableEndlessPaginated,
  EmptyStateProps,
} from '@/components/data-table/data-table-endless-paginated';
import { StickyTableStateContent } from '@/components/data-table/data-table-states';

interface EntityOnboardingTableProps<TEntity> {
  table: Table<TEntity>;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error?: string;
  fixedHeight?: boolean;
  tableId?: string;
  onResizeEnd?: () => void;
  fillLastColumns?: number;
  isAnyFilterApplied?: boolean;
  totalCount?: number;
  /** i18n key for the "no data yet" title (e.g. 'authenticated.onboarding.team:noMembersYet') */
  noDataYetKey: string;
  /** i18n key for the "no data" description */
  noDataDescriptionKey: string;
  /** Render the Add + Import buttons shown in the empty state */
  renderEmptyStateActions: () => React.ReactNode;
}

export function EntityOnboardingTable<TEntity>({
  isAnyFilterApplied,
  totalCount,
  tableId,
  onResizeEnd,
  fillLastColumns,
  noDataYetKey,
  noDataDescriptionKey,
  renderEmptyStateActions,
  ...paginatedTableProps
}: EntityOnboardingTableProps<TEntity>) {
  const { t } = useTranslation('common');

  const renderEmptyState = ({ colSpan }: EmptyStateProps): React.ReactNode => {
    if (isAnyFilterApplied) {
      return (
        <tr>
          <td colSpan={colSpan} className="h-16 w-full px-0">
            <StickyTableStateContent className="px-12 py-4">
              <div className="text-sm">{t('common:noResultsFound')}</div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    if (totalCount === 0) {
      return (
        <tr>
          <td colSpan={colSpan} className="w-full px-0 py-4">
            <StickyTableStateContent className="items-start px-8 text-left">
              <div className="flex flex-col">
                <div className="text-sm font-medium">{t(noDataYetKey)}</div>
                <div className="mt-1 text-xs text-muted-foreground">{t(noDataDescriptionKey)}</div>
                <div className="mt-3 flex gap-2">{renderEmptyStateActions()}</div>
              </div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    return null;
  };

  return (
    <DataTableEndlessPaginated
      {...paginatedTableProps}
      renderEmptyState={renderEmptyState}
      tableId={tableId}
      onResizeEnd={onResizeEnd}
      fillLastColumns={fillLastColumns}
    />
  );
}
